/**
 * Sync Utilities
 * Provides debouncing and state tracking for optimistic updates with backend sync
 */

export type SyncState = 'idle' | 'syncing' | 'failed'

export interface SyncStatus {
  state: SyncState
  lastSync: Date | null
  error: string | null
}

/**
 * Creates a debounced function with sync state tracking
 */
export function createDebouncedSync<T>(
  syncFn: (data: T) => Promise<void>,
  delay: number = 500
) {
  let timeoutId: NodeJS.Timeout | null = null
  let sequenceId = 0
  let syncState: SyncState = 'idle'
  let lastError: string | null = null
  let lastSync: Date | null = null
  let listeners: ((status: SyncStatus) => void)[] = []

  const notifyListeners = () => {
    const status: SyncStatus = {
      state: syncState,
      lastSync,
      error: lastError
    }
    listeners.forEach(listener => listener(status))
  }

  const debouncedSync = async (data: T) => {
    // Clear existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    // Increment sequence to handle out-of-order responses
    const currentSequence = ++sequenceId

    // Set up debounced sync
    timeoutId = setTimeout(async () => {
      // Only proceed if this is still the latest request
      if (currentSequence !== sequenceId) return

      syncState = 'syncing'
      lastError = null
      notifyListeners()

      try {
        await syncFn(data)

        // Only update state if this is still the latest request
        if (currentSequence === sequenceId) {
          syncState = 'idle'
          lastSync = new Date()
          lastError = null
          notifyListeners()
        }
      } catch (error) {
        // Only update state if this is still the latest request
        if (currentSequence === sequenceId) {
          syncState = 'failed'
          lastError = error instanceof Error ? error.message : 'Sync failed'
          notifyListeners()
        }
      }
    }, delay)
  }

  const addListener = (listener: (status: SyncStatus) => void) => {
    listeners.push(listener)

    // Immediately notify with current status
    listener({
      state: syncState,
      lastSync,
      error: lastError
    })

    // Return cleanup function
    return () => {
      listeners = listeners.filter(l => l !== listener)
    }
  }

  const getCurrentStatus = (): SyncStatus => ({
    state: syncState,
    lastSync,
    error: lastError
  })

  const waitForSync = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (syncState === 'idle') {
        resolve()
        return
      }

      if (syncState === 'failed') {
        reject(new Error(lastError || 'Sync failed'))
        return
      }

      const cleanup = addListener((status) => {
        if (status.state === 'idle') {
          cleanup()
          resolve()
        } else if (status.state === 'failed') {
          cleanup()
          reject(new Error(status.error || 'Sync failed'))
        }
      })
    })
  }

  return {
    sync: debouncedSync,
    addListener,
    getCurrentStatus,
    waitForSync,
    // Cancel any pending sync
    cancel: () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }
}

/**
 * Simple debounce utility
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T {
  let timeoutId: NodeJS.Timeout | null = null

  return ((...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    timeoutId = setTimeout(() => {
      func.apply(null, args)
    }, delay)
  }) as T
}